import { useEffect, useState } from 'react';
import { Stack } from '@mui/material';
import { QueryKey, UseMutationResult, useQueryClient } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { useDropzone } from 'react-dropzone';

import { CroppedReplaceableImage, ImageUploadArea, SnackbarError } from '@/components';
import { CreateEntityResponse, onQueryError, onQuerySuccess, useImage } from '@/core';

interface ImageUploadUtilInstantSaveProps {
  entityId: number;
  updateEntityMutation: UseMutationResult<
    AxiosResponse<CreateEntityResponse>,
    unknown,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    { id: number; data: any },
    unknown
  >;
  queryKeysToInvalidateOnSuccess: QueryKey[];
  entityNameForMessages: string;
  getUploadedImage?: (image: File) => void;
  initialImageSrc?: string;
  initialImageId?: number;
}

export const ImageUploadUtilInstantSave = ({
  entityId,
  updateEntityMutation,
  queryKeysToInvalidateOnSuccess,
  entityNameForMessages,
  getUploadedImage,
  initialImageSrc,
  initialImageId,
}: ImageUploadUtilInstantSaveProps) => {
  const [croppedImage, setCroppedImage] = useState<string | null>(null);
  const [isCropperOpen, setIsCropperOpen] = useState(false);
  const [fileSizeError, setFileSizeError] = useState(false);
  const [unsupportedTypeError, setUnsupportedTypeError] = useState(false);
  const queryClient = useQueryClient();

  const {
    mutations: { createImageMutation, deleteImageMutation },
  } = useImage();

  useEffect(() => {
    setCroppedImage(initialImageSrc || null);
  }, [initialImageSrc]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: { 'image/png': ['.png'], 'image/jpg': ['.jpg'], 'image/jpeg': ['.jpeg'] },
    maxSize: 3 * 1024 * 1024, // 3MB
    onDrop: (acceptedFiles, rejectedFiles) => {
      const hasUnsupportedType = rejectedFiles.some(
        (file) => !['image/jpeg', 'image/jpg', 'image/png'].includes(file.file.type),
      );

      if (hasUnsupportedType) {
        setUnsupportedTypeError(true);
        return;
      }

      if (acceptedFiles.length > 0) {
        const uploadedImage = acceptedFiles[0];
        setCroppedImage(URL.createObjectURL(uploadedImage));
        setIsCropperOpen(false);

        const formData = new FormData();
        formData.append('file', uploadedImage);

        createImageMutation.mutate(formData, {
          onSuccess: (response) => {
            onQuerySuccess({
              queryKeysToInvalidate: [],
              successMessage: `Obrázek pro ${entityNameForMessages} byl úspěšně nahrán`,
              queryClient,
            });
            updateEntityMutation.mutate(
              { id: entityId, data: { image_id: response.data.id } },
              {
                onSuccess: () => {
                  onQuerySuccess({
                    queryKeysToInvalidate: queryKeysToInvalidateOnSuccess,
                    successMessage: `Obrázek pro ${entityNameForMessages} byl úspěšně aktualizován`,
                    queryClient,
                  });
                  // Delete old image if it existed
                  if (initialImageId) {
                    deleteImageMutation.mutate(initialImageId);
                  }
                },
                onError: () => {
                  onQueryError({
                    errorMessage: `Obrázek pro ${entityNameForMessages} se nepodařilo aktualizovat`,
                  });
                },
              },
            );
          },
          onError: () => {
            onQueryError({
              errorMessage: `Obrázek pro ${entityNameForMessages} se nepodařilo nahrát`,
            });
          },
        });

        if (getUploadedImage) {
          getUploadedImage(uploadedImage);
        }
        setFileSizeError(false);
        setUnsupportedTypeError(false);
      }
    },
    onDropRejected: () => {
      setFileSizeError(true);
    },
  });

  const handleCloseError = () => {
    setFileSizeError(false);
    setUnsupportedTypeError(false);
  };

  const handleChangePictureClick = () => {
    setCroppedImage(null);
  };

  return (
    <Stack gap={2}>
      {!croppedImage && (
        <ImageUploadArea
          getRootProps={getRootProps}
          getInputProps={getInputProps}
          truncatedForm={isDragActive || isCropperOpen}
        />
      )}

      <SnackbarError
        open={fileSizeError}
        onClose={handleCloseError}
        alertMessage={'Soubor je příliš velký. Maximální velikost je 3MB.'}
      />

      <SnackbarError
        open={unsupportedTypeError}
        onClose={handleCloseError}
        alertMessage={'Nepodporovaný typ souboru. Povolené formáty jsou JPEG a PNG.'}
      />

      {croppedImage && (
        <CroppedReplaceableImage
          croppedImageSrc={croppedImage}
          handleChangePictureClick={handleChangePictureClick}
        />
      )}
    </Stack>
  );
};
